/**
 * Region Service
 * Handles CRUD operations for regions in Firestore
 * Note: Regions are currently admin-only, preparing for future public release
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../config/firebase";
import {
  Region,
  RegionCode,
  RegionWithStats,
  RegionSummary,
  CreateRegionInput,
  UpdateRegionInput,
  RegionFilter,
  DEFAULT_REGION_FEATURES,
} from "../types/regions";
import {
  ALL_REGIONS,
  getRegionByCode as getStaticRegionByCode,
  getRegionById as getStaticRegionById,
  filterRegions as filterStaticRegions,
} from "../data/regions";

const REGIONS_COLLECTION = "regions";

/**
 * Convert region to Firestore-compatible format
 * Firestore doesn't support nested arrays, so we flatten coordinates
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const convertRegionToFirestore = (region: Region): any => {
  // Deep clone to avoid mutating original
  const firestoreRegion = JSON.parse(JSON.stringify(region));

  // Flatten boundary coordinates (convert nested arrays to JSON string)
  if (firestoreRegion.boundary?.coordinates) {
    firestoreRegion.boundary.coordinatesJson = JSON.stringify(
      firestoreRegion.boundary.coordinates
    );
    delete firestoreRegion.boundary.coordinates;
  }

  // Flatten historical periods boundary coordinates if present
  if (firestoreRegion.historicalPeriods) {
    firestoreRegion.historicalPeriods = firestoreRegion.historicalPeriods.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (period: any) => {
        if (period.boundary?.coordinates) {
          return {
            ...period,
            boundary: {
              ...period.boundary,
              coordinatesJson: JSON.stringify(period.boundary.coordinates),
              coordinates: undefined,
            },
          };
        }
        return period;
      }
    );
  }

  return firestoreRegion;
};

/**
 * Convert Firestore data back to Region format
 * Restores nested arrays from JSON strings
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const convertFirestoreToRegion = (firestoreData: any): Region => {
  const region = { ...firestoreData };

  // Restore boundary coordinates from JSON string
  if (region.boundary?.coordinatesJson) {
    region.boundary.coordinates = JSON.parse(region.boundary.coordinatesJson);
    delete region.boundary.coordinatesJson;
  }

  // Restore historical periods boundary coordinates if present
  if (region.historicalPeriods) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    region.historicalPeriods = region.historicalPeriods.map((period: any) => {
      if (period.boundary?.coordinatesJson) {
        const coordinates = JSON.parse(period.boundary.coordinatesJson);
        const { coordinatesJson, ...restBoundary } = period.boundary;
        return {
          ...period,
          boundary: {
            ...restBoundary,
            coordinates,
          },
        };
      }
      return period;
    });
  }

  return region as Region;
};

/**
 * Initialize regions collection with static data
 * Call this to seed the database with predefined regions
 */
export const initializeRegions = async (): Promise<void> => {
  try {
    const regionsRef = collection(db, REGIONS_COLLECTION);

    for (const region of ALL_REGIONS) {
      const regionDoc = doc(regionsRef, region.id);
      const existingDoc = await getDoc(regionDoc);

      if (!existingDoc.exists()) {
        const firestoreRegion = convertRegionToFirestore(region);
        await setDoc(regionDoc, {
          ...firestoreRegion,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
    }
  } catch (error) {
    console.error("Error initializing regions:", error);
    throw error;
  }
};

/**
 * Get all regions from Firestore
 * Falls back to static data if Firestore is unavailable
 */
export const getAllRegions = async (): Promise<Region[]> => {
  try {
    const regionsRef = collection(db, REGIONS_COLLECTION);
    const q = query(regionsRef, orderBy("order", "asc"));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      // Fall back to static data if no regions in Firestore
      return ALL_REGIONS;
    }

    return querySnapshot.docs.map((doc) =>
      convertFirestoreToRegion({
        id: doc.id,
        ...doc.data(),
      })
    );
  } catch (error) {
    console.error("Error fetching regions from Firestore:", error);
    // Fall back to static data
    return ALL_REGIONS;
  }
};

/**
 * Get a region by ID
 */
export const getRegionById = async (regionId: string): Promise<Region | null> => {
  try {
    const regionDoc = await getDoc(doc(db, REGIONS_COLLECTION, regionId));

    if (regionDoc.exists()) {
      return convertFirestoreToRegion({ id: regionDoc.id, ...regionDoc.data() });
    }

    // Fall back to static data
    const staticRegion = getStaticRegionById(regionId);
    return staticRegion || null;
  } catch (error) {
    console.error("Error fetching region:", error);
    const staticRegion = getStaticRegionById(regionId);
    return staticRegion || null;
  }
};

/**
 * Get a region by code
 */
export const getRegionByCode = async (code: RegionCode): Promise<Region | null> => {
  try {
    const regionsRef = collection(db, REGIONS_COLLECTION);
    const q = query(regionsRef, where("code", "==", code));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const docData = querySnapshot.docs[0];
      return convertFirestoreToRegion({ id: docData.id, ...docData.data() });
    }

    // Fall back to static data
    const staticRegion = getStaticRegionByCode(code);
    return staticRegion || null;
  } catch (error) {
    console.error("Error fetching region by code:", error);
    const staticRegion = getStaticRegionByCode(code);
    return staticRegion || null;
  }
};

/**
 * Create a new region
 */
export const createRegion = async (input: CreateRegionInput): Promise<Region> => {
  try {
    const regionId = `region-${input.code}-${Date.now()}`;
    const now = new Date().toISOString();

    const newRegion: Region = {
      id: regionId,
      code: input.code,
      name: input.name,
      localizedNames: input.localizedNames || { en: input.name },
      description: input.description,
      boundary: input.boundary,
      viewport: input.viewport,
      area: input.area,
      status: input.status || "hidden",
      visibility: input.visibility || "admin",
      isDefault: input.isDefault || false,
      features: {
        ...DEFAULT_REGION_FEATURES,
        ...input.features,
      },
      defaultMapStyle: input.defaultMapStyle,
      themeColor: input.themeColor,
      flagEmoji: input.flagEmoji,
      thumbnailUrl: input.thumbnailUrl,
      historicalPeriods: input.historicalPeriods,
      parentRegions: input.parentRegions,
      childRegions: input.childRegions,
      createdAt: now,
      updatedAt: now,
      order: input.order,
      tags: input.tags,
    };

    const firestoreRegion = convertRegionToFirestore(newRegion);
    await setDoc(doc(db, REGIONS_COLLECTION, regionId), firestoreRegion);

    return newRegion;
  } catch (error) {
    console.error("Error creating region:", error);
    throw error;
  }
};

/**
 * Update an existing region
 * If the region doesn't exist in Firestore, it will be created from static data first
 */
export const updateRegion = async (
  regionId: string,
  updates: UpdateRegionInput
): Promise<void> => {
  try {
    const regionRef = doc(db, REGIONS_COLLECTION, regionId);
    const existingDoc = await getDoc(regionRef);

    // If region doesn't exist in Firestore, create it from static data first
    if (!existingDoc.exists()) {
      const staticRegion = getStaticRegionById(regionId);
      if (staticRegion) {
        const firestoreRegion = convertRegionToFirestore(staticRegion);
        await setDoc(regionRef, {
          ...firestoreRegion,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      } else {
        throw new Error(`Region ${regionId} not found in static data`);
      }
    }

    // Now update the region
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updateData: { [key: string]: any } = {
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    // Handle nested localizedNames update
    if (updates.localizedNames) {
      const currentDoc = await getDoc(regionRef);
      if (currentDoc.exists()) {
        const existingData = currentDoc.data();
        updateData.localizedNames = {
          ...existingData.localizedNames,
          ...updates.localizedNames,
        };
      }
    }

    // Handle nested features update
    if (updates.features) {
      const currentDoc = await getDoc(regionRef);
      if (currentDoc.exists()) {
        const existingData = currentDoc.data();
        updateData.features = {
          ...existingData.features,
          ...updates.features,
        };
      }
    }

    await updateDoc(regionRef, updateData);
  } catch (error) {
    console.error("Error updating region:", error);
    throw error;
  }
};

/**
 * Delete a region
 */
export const deleteRegion = async (regionId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, REGIONS_COLLECTION, regionId));
  } catch (error) {
    console.error("Error deleting region:", error);
    throw error;
  }
};

/**
 * Get regions with statistics
 */
export const getRegionsWithStats = async (): Promise<RegionWithStats[]> => {
  try {
    const regions = await getAllRegions();
    const regionsWithStats: RegionWithStats[] = [];

    for (const region of regions) {
      // Get map statistics for this region
      const mapsRef = collection(db, "maps");
      const mapsQuery = query(mapsRef, where("country", "==", region.name));
      const mapsSnapshot = await getDocs(mapsQuery);

      let totalMaps = 0;
      let publicMaps = 0;
      let totalViews = 0;
      const userSet = new Set<string>();
      const activeUserSet = new Set<string>();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      mapsSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        totalMaps++;
        if (data.isPublic) publicMaps++;
        totalViews += data.viewsCount || 0;
        if (data.userId) {
          userSet.add(data.userId);
          const createdAt = new Date(data.createdAt);
          if (createdAt >= thirtyDaysAgo) {
            activeUserSet.add(data.userId);
          }
        }
      });

      regionsWithStats.push({
        ...region,
        mapsCount: totalMaps,
        publicMapsCount: publicMaps,
        usersCount: userSet.size,
        stats: {
          totalMaps,
          publicMaps,
          totalUsers: userSet.size,
          activeUsers: activeUserSet.size,
          totalViews,
        },
      });
    }

    return regionsWithStats;
  } catch (error) {
    console.error("Error fetching regions with stats:", error);
    // Return regions without stats as fallback
    const regions = await getAllRegions();
    return regions.map((region) => ({
      ...region,
      mapsCount: 0,
      publicMapsCount: 0,
      usersCount: 0,
      stats: {
        totalMaps: 0,
        publicMaps: 0,
        totalUsers: 0,
        activeUsers: 0,
        totalViews: 0,
      },
    }));
  }
};

/**
 * Get region summaries (lightweight version for listings)
 */
export const getRegionSummaries = async (): Promise<RegionSummary[]> => {
  const regions = await getAllRegions();
  return regions.map((region) => ({
    id: region.id,
    code: region.code,
    name: region.name,
    localizedNames: region.localizedNames,
    status: region.status,
    visibility: region.visibility,
    flagEmoji: region.flagEmoji,
    thumbnailUrl: region.thumbnailUrl,
    viewport: region.viewport,
    order: region.order,
  }));
};

/**
 * Filter regions based on criteria
 */
export const filterRegions = async (filter: RegionFilter): Promise<Region[]> => {
  try {
    // For now, use static filtering since Firestore queries have limitations
    // In production, you might want to implement more sophisticated querying
    return filterStaticRegions(filter);
  } catch (error) {
    console.error("Error filtering regions:", error);
    return filterStaticRegions(filter);
  }
};

/**
 * Update region status
 */
export const updateRegionStatus = async (
  regionId: string,
  status: Region["status"]
): Promise<void> => {
  await updateRegion(regionId, { status });
};

/**
 * Update region visibility
 */
export const updateRegionVisibility = async (
  regionId: string,
  visibility: Region["visibility"]
): Promise<void> => {
  await updateRegion(regionId, { visibility });
};

/**
 * Set a region as the default
 */
export const setDefaultRegion = async (regionId: string): Promise<void> => {
  try {
    // First, remove default from all other regions
    const regions = await getAllRegions();
    for (const region of regions) {
      if (region.isDefault && region.id !== regionId) {
        await updateRegion(region.id, { isDefault: false });
      }
    }

    // Set the new default
    await updateRegion(regionId, { isDefault: true });
  } catch (error) {
    console.error("Error setting default region:", error);
    throw error;
  }
};

/**
 * Get visible regions for a user based on their role
 */
export const getVisibleRegions = async (
  userRole: "public" | "user" | "approver" | "admin"
): Promise<Region[]> => {
  const regions = await getAllRegions();

  // Map user roles to visibility levels
  const roleToVisibility: Record<string, string[]> = {
    public: ["public"],
    user: ["public", "registered"],
    approver: ["public", "registered", "premium"],
    admin: ["public", "registered", "premium", "admin"],
  };

  const allowedVisibilities = roleToVisibility[userRole] || ["public"];

  return regions.filter(
    (region) =>
      allowedVisibilities.includes(region.visibility) &&
      region.status !== "hidden" &&
      region.status !== "deprecated"
  );
};

